import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar } from '@mui/material';
import { CallWithAuth } from '../../../actions/apiAction';
import { DELETE_CHAT_MESSAGES, GET_CREATOR_DETAILS } from '../../../utils/apiUrl';
import Cookie from '../../../utils/Cookie';

const ClearChatModal = ({ open, onClose, onConfirm, loading }) => {
    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.55)' }}
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-card p-6 shadow-2xl"
                style={{ animation: 'modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1) both' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Icon */}
                <div className="flex justify-center mb-4">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(249,13,13,0.12)', border: '1.5px solid rgba(249,13,13,0.25)' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width={28} height={28} viewBox="0 0 24 24" fill="none"
                            stroke="#f90d0d" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                    </div>
                </div>

                {/* Text */}
                <h2 className="text-center text-lg font-bold text-foreground">Delete Conversation?</h2>
                <p className="text-center text-sm text-muted-foreground mt-2 leading-relaxed">
                    Are you sure you want to delete this conversation?
                    <br />
                    <span className="text-[#f90d0d]/80 font-medium">This action cannot be undone.</span>
                </p>

                {/* Actions */}
                <div className="flex gap-3 mt-6">
                    <button
                        className="flex-1 rounded-full border border-white/15 text-foreground/80 font-semibold py-2.5 text-sm hover:bg-white/5 transition-colors"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        className="flex-1 rounded-full font-semibold py-2.5 text-sm text-white transition-opacity hover:opacity-85 flex items-center justify-center gap-2"
                        style={{ background: 'linear-gradient(135deg,#c0392b,#f90d0d)' }}
                        onClick={onConfirm}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" width={14} height={14} fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                                Deleting…
                            </>
                        ) : 'Yes, Delete'}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes modalIn {
                    from { opacity: 0; transform: scale(0.88) translateY(12px); }
                    to   { opacity: 1; transform: scale(1)    translateY(0);    }
                }
            `}</style>
        </div>
    );
};

const CreatorProfile = () => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const query = useMemo(() => new URLSearchParams(search), [search]);
    const creatorId = query.get('id');
    const chatId = Cookie.getCookie('activeChatId');
    const [loading, setLoading] = useState(true);
    const [creatorDetails, setCreatorDetails] = useState(null);
    const [showClearModal, setShowClearModal] = useState(false);
    const [clearLoading, setClearLoading] = useState(false);

    const getCreatorDetails = async () => {
        if (!creatorId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const response = await CallWithAuth('GET', GET_CREATOR_DETAILS(creatorId));
        if (response?.data) {
            setCreatorDetails(response.data);
        }
        setLoading(false);
    };

    useEffect(() => {
        getCreatorDetails();
    }, [creatorId]);

    const handleClearChat = async () => {
        if (!chatId) return;
        setClearLoading(true);
        try {
            await CallWithAuth('DELETE', DELETE_CHAT_MESSAGES(chatId));
        } catch (err) {
            console.error('Failed to clear chat history:', err);
        } finally {
            setClearLoading(false);
            setShowClearModal(false);
            navigate('/user/creator-list');

        }
    };

    const rawUsername = creatorDetails?.username?.trim();
    const creatorHandle = rawUsername
        ? (rawUsername.startsWith('@') ? rawUsername : `@${rawUsername}`)
        : null;

    const fullName = `${creatorDetails?.first_name || ''} ${creatorDetails?.last_name || ''}`.trim();
    const creatorName = fullName || rawUsername || 'Creator';

    return (
        <>
            <Helmet>
                <title>{creatorName} | Profile | TLK</title>
            </Helmet>

            <ClearChatModal
                open={showClearModal}
                onClose={() => !clearLoading && setShowClearModal(false)}
                onConfirm={handleClearChat}
                loading={clearLoading}
            />

            <div className="min-h-screen bg-chat-bg flex justify-center">
                <div className="w-full max-w-md bg-card min-h-screen border-x border-white/10">
                    <div className="relative">
                        <div className="h-44 bg-gradient-to-r from-[#393b5f] via-[#6a3e7c] to-[#a44a74] overflow-hidden">
                            {creatorDetails?.profile_picture_url && (
                                <img
                                    src={creatorDetails.profile_picture_url}
                                    alt={creatorName}
                                    className="w-full h-full object-cover opacity-45"
                                />
                            )}
                        </div>

                        <button
                            className="absolute top-4 left-4 w-9 h-9 rounded-full bg-black/35 text-white flex items-center justify-center"
                            onClick={() => navigate(-1)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="m15 18-6-6 6-6" />
                            </svg>
                        </button>

                        <div className="absolute -bottom-12 left-6">
                            <Avatar
                                alt={creatorName}
                                src={creatorDetails?.profile_picture_url}
                                sx={{ width: 96, height: 96, border: '4px solid #fff', fontSize: 36 }}
                            >
                                {creatorDetails?.first_name?.charAt(0) || 'C'}
                            </Avatar>
                        </div>
                    </div>

                    <div className="pt-16 px-6 pb-24">
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="loaderBox" style={{ position: 'unset', margin: 0, padding: 0, height: 'unset', width: 'unset' }}>
                                    <div className="loader" style={{ width: 36, height: 36 }} />
                                </div>
                            </div>
                        ) : (
                            <>
                                <h1 className="text-2xl font-bold text-foreground">{creatorName}</h1>
                                {creatorHandle && (
                                    <p className="text-sm text-muted-foreground mt-1">{creatorHandle}</p>
                                )}

                                <p className="text-sm text-foreground/90 mt-4 leading-relaxed">
                                    {creatorDetails?.description || 'Welcome to my profile. Follow to unlock updates, premium content, and direct chat moments.'}
                                </p>

                                <div className="grid grid-cols-3 gap-3 mt-5">
                                    <div className="rounded-xl border border-white/10 bg-black/10 p-3 text-center">
                                        <p className="text-base font-semibold text-foreground">{creatorDetails?.posts_count ?? 0}</p>
                                        <p className="text-xs text-muted-foreground">Posts</p>
                                    </div>
                                    <div className="rounded-xl border border-white/10 bg-black/10 p-3 text-center">
                                        <p className="text-base font-semibold text-foreground">{creatorDetails?.media_count ?? 0}</p>
                                        <p className="text-xs text-muted-foreground">Media</p>
                                    </div>
                                    <div className="rounded-xl border border-white/10 bg-black/10 p-3 text-center">
                                        <p className="text-base font-semibold text-foreground">{creatorDetails?.likes_count ?? 0}</p>
                                        <p className="text-xs text-muted-foreground">Likes</p>
                                    </div>
                                </div>

                                <div className="mt-6 space-y-3">
                                    <button
                                        className="w-full rounded-full bg-primary text-white font-semibold py-3 hover:opacity-90 transition-opacity"
                                        onClick={() => navigate('/user/subscription-plans')}
                                    >
                                        Subscribe
                                    </button>
                                    <button
                                        className="w-full rounded-full border border-primary/40 text-primary font-semibold py-3 hover:bg-primary/10 transition-colors"
                                        onClick={() => navigate(`/user/chat?id=${creatorId}`)}
                                    >
                                        Message Creator
                                    </button>
                                    <button
                                        className="w-full rounded-full border border-primary/40 text-[#f90d0d] font-semibold py-3 hover:bg-primary/10 transition-colors"
                                        onClick={() => setShowClearModal(true)}
                                    >
                                        Clear Chat History
                                    </button>
                                </div>

                                <div className="mt-7 border-t border-white/10 pt-5">
                                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                                        <span>Content feed</span>
                                        <span>Locked for non-subscribers</span>
                                    </div>
                                    <div className="mt-3 rounded-2xl border border-white/10 bg-black/10 p-6 text-center">
                                        <div className="text-4xl mb-2">🔒</div>
                                        <p className="text-sm text-foreground/85">Subscribe to see exclusive posts, media, and premium updates.</p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CreatorProfile;